// Soundscapes and Programs data.
// Each soundscape pairs a short Sora-generated visual loop (silent video)
// with a 1-hour bespoke ambient audio track served from the FastAPI backend.

export const soundscapes = [
  {
    id: 'breathe-with-me',
    name: 'Breathe With Me',
    description: 'Guided visual breath with Rowdy',
    accent: '#5b7a65',
    image: 'https://images.unsplash.com/photo-1518623489648-a173ef7824f3?w=800&h=600&fit=crop',
  },
  {
    id: 'the-cabin',
    name: 'The Cabin',
    description: 'A quiet cabin retreat with Rowdy',
    accent: '#6b5a3e',
    image: 'https://images.unsplash.com/photo-1449158743715-0a90ebb6d2d8?w=800&h=600&fit=crop',
  },
  {
    id: 'cabin-in-the-rain',
    name: 'Cabin In The Rain',
    description: 'Rain on the cabin roof — hours of calm',
    accent: '#4a5b6a',
    image: 'https://images.unsplash.com/photo-1428592953211-077101b2021b?w=800&h=600&fit=crop',
  },
  {
    id: 'the-forest',
    name: 'The Forest',
    description: 'Deep in the woods — slow, alive, still',
    accent: '#4c6b47',
    image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop',
  },
  {
    id: 'white-noise-video',
    name: 'White Noise',
    description: 'Steady white noise for deep rest',
    accent: '#5d7ea8',
    image: 'https://images.unsplash.com/photo-1483664852095-d6cc6870702d?w=800&h=600&fit=crop',
  },
  {
    id: 'lofi-and-rain',
    name: 'Lofi and Rain',
    description: 'Lofi beats over gentle rain — perfect for focus',
    accent: '#6a5f8a',
    image: 'https://images.unsplash.com/photo-1519692933481-e162a57d6721?w=800&h=600&fit=crop',
  },
  {
    id: 'soft-rain',
    name: 'Soft Rain',
    description: 'Light rain ambient · 1 hour',
    accent: '#7ba6c4',
    image: 'https://images.unsplash.com/photo-1438449805896-28a666819a20?w=800&h=600&fit=crop',
    video: '/api/static/soundscapes/soft-rain.mp4',
    audio: '/api/static/audio/soft-rain.mp3',
  },
  {
    id: 'waterfall',
    name: 'Waterfall',
    description: 'Cascading waterfall · 1 hour',
    accent: '#5d8aa8',
    image: 'https://images.unsplash.com/photo-1432405972618-c60b0225b8f9?w=800&h=600&fit=crop',
    video: '/api/static/soundscapes/waterfall.mp4',
    audio: '/api/static/audio/waterfall.mp3',
  },
  {
    id: 'crackling-fire',
    name: 'Crackling Fire',
    description: 'Warm campfire · 1 hour',
    accent: '#c97a3a',
    image: 'https://images.unsplash.com/photo-1543589077-47d81606c1bf?w=800&h=600&fit=crop',
    video: '/api/static/soundscapes/crackling-fire.mp4',
    audio: '/api/static/audio/crackling-fire.mp3',
  },
  {
    id: 'white-noise',
    name: 'Soft White Noise',
    description: 'Underwater ambience · 1 hour',
    accent: '#5d7ea8',
    image: 'https://images.unsplash.com/photo-1551244072-5d12893278ab?w=800&h=600&fit=crop',
    video: '/api/static/soundscapes/white-noise.mp4',
    audio: '/api/static/audio/white-noise.mp3',
  },
];

export const getSoundscapeById = (id) => soundscapes.find((s) => s.id === id);


// ---- Programs / Series — multi-session courses ------------------------------
// Each session references either a postId or a breathingId to reuse content.

export const programs = [
  {
    id: '7-days-of-calm',
    name: '7 Days of Calm',
    tagline: 'A gentle week to reset your nervous system',
    description:
      'One short reflection a day for seven days. Build a daily mindful habit at your own pace.',
    image: 'https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?w=800&h=600&fit=crop',
    accent: '#D6AD60',
    sessions: [
      { id: 's1', day: 1, title: 'Begin with breath', kind: 'breath', ref: 'gentle-calm-circle', minutes: 3 },
      { id: 's2', day: 2, title: 'Look on the bright side', kind: 'post', ref: 'look-on-the-bright-side', minutes: 4 },
      { id: 's3', day: 3, title: 'Loosen the shoulders', kind: 'breath', ref: 'shoulder-relax-breath', minutes: 4 },
      { id: 's4', day: 4, title: 'Anxiety meditation', kind: 'post', ref: 'anxiety-meditation', minutes: 6 },
      { id: 's5', day: 5, title: 'Long exhale relaxation', kind: 'breath', ref: 'long-exhale-relaxation', minutes: 5 },
      { id: 's6', day: 6, title: 'Keep going', kind: 'post', ref: 'keep-going', minutes: 3 },
      { id: 's7', day: 7, title: 'Closing reflection', kind: 'breath', ref: 'final-calm-breath', minutes: 5 },
    ],
  },
  {
    id: '21-day-sleep-reset',
    name: '21 Day Sleep Reset',
    tagline: 'Three weeks of evening wind-downs',
    description:
      'Pair a sleep story with an end-of-day breath each evening. Re-pattern how you fall asleep.',
    image: 'https://images.unsplash.com/photo-1502635385003-ee1e6a1a742d?w=800&h=600&fit=crop',
    accent: '#5d8aa8',
    sessions: [
      { id: 's1', day: 1, title: 'Workday release', kind: 'breath', ref: 'workday-release-circle', minutes: 4 },
      { id: 's2', day: 2, title: 'The whispering woods', kind: 'post', ref: 'the-wisdom-of-the-whispering-woods', minutes: 12 },
      { id: 's3', day: 3, title: 'Evening transition', kind: 'breath', ref: 'evening-transition-breath', minutes: 4 },
      { id: 's4', day: 4, title: 'Soft landing', kind: 'breath', ref: 'soft-landing-breath', minutes: 4 },
      { id: 's5', day: 5, title: 'Peaceful pause', kind: 'breath', ref: 'peaceful-pause-breath', minutes: 5 },
      { id: 's6', day: 6, title: 'Unwind', kind: 'breath', ref: 'unwind-breath', minutes: 5 },
      { id: 's7', day: 7, title: 'Let go', kind: 'breath', ref: 'let-go-breath', minutes: 4 },
    ],
  },
  {
    id: 'breath-foundations',
    name: 'Breath Foundations',
    tagline: 'Seven essential patterns in seven days',
    description:
      'Build the foundation of a breathing practice you can return to anytime, anywhere.',
    image: 'https://images.unsplash.com/photo-1490730141103-6cac27aaab94?w=800&h=600&fit=crop',
    accent: '#6b8e4e',
    sessions: [
      { id: 's1', day: 1, title: '4-In / 4-Out', kind: 'breath', ref: '4-in-4-out', minutes: 3 },
      { id: 's2', day: 2, title: '5-In / 5-Out', kind: 'breath', ref: '5-in-5-out', minutes: 4 },
      { id: 's3', day: 3, title: '3-In / 6-Out', kind: 'breath', ref: '3-in-6-out', minutes: 4 },
      { id: 's4', day: 4, title: 'Box Breath (4-4-4)', kind: 'breath', ref: '4-4-4-box-breath', minutes: 5 },
      { id: 's5', day: 5, title: '5-5-5 Calm', kind: 'breath', ref: '5-5-5-calm-breath', minutes: 5 },
      { id: 's6', day: 6, title: 'Relaxation Ladder', kind: 'breath', ref: 'relaxation-ladder', minutes: 5 },
      { id: 's7', day: 7, title: 'Ground & Breathe', kind: 'breath', ref: 'ground-and-breathe', minutes: 5 },
    ],
  },
];

export const getProgramById = (id) => programs.find((p) => p.id === id);
